import React, { useEffect, useState } from 'react';
import { Box, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import DashboardCard from './DashboardCard';
import BiweeklyTopline from './BiweeklyTopline';
import api from '../../services/api';
import { useWorkspace } from '../../utils/WorkspaceContext';
import { useTaskOverlay } from '../../utils/TaskOverlayContext';

// The column count is kept in localStorage so the dashboard looks the same
// after a refresh. The cards themselves are always in the same fixed order.
const COLS_KEY = 'dashboard-cols';
const COL_OPTIONS = [1, 2, 3];

const readCols = () => {
    try {
        const stored = Number(JSON.parse(localStorage.getItem(COLS_KEY)));
        return COL_OPTIONS.includes(stored) ? stored : 2;
    } catch {
        return 2;
    }
};

const Home = () => {
    const [pendingTasks, setPendingTasks] = useState([]);
    const [taskChart, setTaskChart] = useState([]);
    const [tasksCompleted, setTasksCompleted] = useState([]);
    const [meetingsToday, setMeetingsToday] = useState([]);
    const [biweekly, setBiweekly] = useState(null);
    const [cols, setCols] = useState(readCols);
    const { currentWorkspace } = useWorkspace();
    // Opens a task as a page or a drawer, per the user's display preference.
    const { openTaskDetail } = useTaskOverlay();

    useEffect(() => {
        if (!currentWorkspace?.id) return;
        const fetchDashboardData = async () => {
            try {
                const params = { workspace_id: currentWorkspace.id }
                const [
                    tasksCompletedRes,
                    pendingTasksRes,
                    taskChartRes,
                    meetingsTodayRes,
                    biweeklyRes,
                ] = await Promise.all([
                    api.get("dashboard/task-completed/", { params }),
                    api.get("dashboard/do-today/", { params }),
                    api.get("timelog-chart/"),
                    api.get("dashboard/meetings/"),
                    api.get("dashboard/biweekly-topline/", { params }),
                ]);

                if (tasksCompletedRes.status === 200) setTasksCompleted(tasksCompletedRes.data.payload);
                if (pendingTasksRes.status === 200) setPendingTasks(pendingTasksRes.data.payload);
                if (taskChartRes.status === 200) setTaskChart(taskChartRes.data.data);
                if (meetingsTodayRes.status === 200) setMeetingsToday(meetingsTodayRes.data.payload);
                if (biweeklyRes.status === 200) setBiweekly(biweeklyRes.data.payload);
            } catch (error) {
                console.error("Error fetching dashboard data:", error);
            }
        };

        fetchDashboardData();
    }, [currentWorkspace?.id]);

    useEffect(() => {
        try {
            localStorage.setItem(COLS_KEY, JSON.stringify(cols));
        } catch {
            // Storage unavailable; the choice just won't survive a refresh.
        }
    }, [cols]);

    const openTaskRow = (row) => openTaskDetail(row.id);
    const openChartTask = (row) => openTaskDetail(row.task_id);

    return (
        // No fixed height or overflow here: the page scrolls with the browser
        // scrollbar rather than inside a nested container.
        <Box sx={{ flexGrow: 1, p: 2, maxWidth: '100%' }}>
            <BiweeklyTopline data={biweekly} />

            <FormControl sx={{ mb: 2, minWidth: 120 }}>
                <InputLabel>Columns</InputLabel>
                <Select
                    value={cols}
                    label="Columns"
                    onChange={(event) => setCols(event.target.value)}
                >
                    {COL_OPTIONS.map((n) => (
                        <MenuItem key={n} value={n}>{n} Column{n > 1 ? 's' : ''}</MenuItem>
                    ))}
                </Select>
            </FormControl>

            {/* Fixed order: Tasks Completed, Tasks Pending, Time Log Chart, Meetings Today. */}
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: `repeat(${cols}, minmax(0, 1fr))` },
                    alignItems: 'start',
                }}
            >
                <DashboardCard
                    title="Tasks Completed"
                    type="list"
                    data={tasksCompleted}
                    columns={['name', 'line_up']}
                    onRowClick={openTaskRow}
                />
                <DashboardCard
                    title="Tasks Pending"
                    type="list"
                    data={pendingTasks}
                    columns={['name', 'line_up']}
                    onRowClick={openTaskRow}
                />
                <DashboardCard
                    title="Time Log Chart"
                    type="bar"
                    data={taskChart}
                    onRowClick={openChartTask}
                />
                <DashboardCard
                    title="Meetings Today"
                    type="list"
                    data={meetingsToday}
                    columns={['meeting_name', 'attendees']}
                />
            </Box>
        </Box>
    );
};

export default Home;
